import { useState } from 'react'
import { quizService, QuizResult } from '../lib/supabase'

export interface UseQuizResultsReturn {
  isLoading: boolean
  isSaving: boolean
  error: string | null
  savedResult: QuizResult | null
  saveQuizResult: (profileData: any, answers: any, email?: string) => Promise<QuizResult>
  getQuizResult: (shareToken: string) => Promise<QuizResult>
  updateResultEmail: (id: string, email: string) => Promise<QuizResult>
  generateShareUrl: (shareToken: string) => string
  reset: () => void
}

export function useQuizResults(): UseQuizResultsReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedResult, setSavedResult] = useState<QuizResult | null>(null)

  const saveQuizResult = async (profileData: any, answers: any, email?: string): Promise<QuizResult> => {
    setIsSaving(true)
    setError(null)

    try {
      const result = await quizService.saveQuizResult(profileData, answers, email)
      setSavedResult(result)
      
      // Email will be automatically sent via Supabase trigger if email is provided
      if (email) {
        console.log('Quiz result saved with email - automatic email will be sent via trigger')
      }
      
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save quiz result'
      setError(errorMessage)
      throw err
    } finally {
      setIsSaving(false)
    }
  }

  const getQuizResult = async (shareToken: string): Promise<QuizResult> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await quizService.getQuizResultByToken(shareToken)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load quiz result'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const updateResultEmail = async (id: string, email: string): Promise<QuizResult> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await quizService.updateQuizResultEmail(id, email)
      setSavedResult(result)
      
      // Email will be automatically sent via Supabase trigger when email is updated
      console.log('Email updated - automatic email will be sent via trigger')
      
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update email'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const generateShareUrl = (shareToken: string): string => {
    return `${window.location.origin}/share/${shareToken}`
  }

  const reset = () => {
    setIsLoading(false)
    setIsSaving(false)
    setError(null)
    setSavedResult(null)
  }

  return {
    isLoading,
    isSaving,
    error,
    savedResult,
    saveQuizResult,
    getQuizResult,
    updateResultEmail,
    generateShareUrl,
    reset
  }
}